import { Fragment, useRef, forwardRef, useState } from 'react'
import PropTypes from 'prop-types'
import { Formik, Form } from 'formik'
import to from 'await-to-js'
import * as yup from 'yup'
import { useTheme } from '@emotion/react'
import { graphql } from 'gatsby'
import qs from 'qs'
import {
  Button,
  List,
  ListItem,
  Container,
  Section,
  Badge,
  SectionTitle,
  Tooltip,
  Alert,
  Close,
} from 'chaoskit/src/components'
import FormikField from 'chaoskit/src/components/FormikField'
import { gradient, text, link, misc } from 'chaoskit/src/assets/styles/utility'
import { generateGradient } from 'chaoskit/src/assets/styles/utility/gradient'
import useIntersection from 'react-use/lib/useIntersection'
import axios from 'axios'
import { generateButtonGradient } from 'chaoskit/src/components/Button'

import {
  ProductSection,
  ProductGridBlock,
  ProductGridBlockContent,
} from '~partials/product/UI'
import BaseLayout from '~layouts/BaseLayout'
import Hero, {
  HeroContent,
  HeroTitleWrapper,
  HeroTitle,
  HeroTitleSub,
} from '~partials/Hero'
import Data from '~components/Data'
import HelmetSEO from '~partials/global/HelmetSEO'
import { StylesHeaderVariables } from '~partials/header/UI'
import { tiers, features, addons } from '~partials/pricing/data'
import Icon from '~components/Icon'
import GradientList from '~partials/global/GradientList'
import questionCircle from '~icons/question.svg'
import heroBlob from '~media/pricing/hero-blob.svg'
import { ReactComponent as FreeSVG } from '~media/pricing/free.svg'
import freeBlob from '~media/pricing/free-blob.svg'
import tableBlob from '~media/pricing/table-blob.svg'
import tableBlobSmall from '~media/pricing/table-blob-small.svg'
import ButtonLink from '~components/ButtonLink'
import TextLink from '~components/TextLink'
import { stickyIt } from '~styles/utility'
import track from '~helpers/track'
import phoneValidator from '~helpers/phone-validator'
import '~helpers/yup-corporate-email'
import BotField from '~components/BotField'

const StylesPricingVariables = (theme) => ({
  heroOffset: 250 + theme.space.xlarge,
})

const openRequestDemoSection = () => {
  const $requestDemo = document.querySelector('#request-demo')

  if ($requestDemo) {
    $requestDemo.style.display = 'block'
  }
}

const closeRequestDemoSection = () => {
  const $requestDemo = document.querySelector('#request-demo')

  if ($requestDemo) {
    $requestDemo.style.display = 'none'
  }
}

const TryForFreeButton = (props) => (
  <ButtonLink
    url="/gremlin-free-software/?ref=pricing"
    type="primary"
    {...props}
  >
    Try for free
  </ButtonLink>
)

// Variation of `<Button />` component with gradient customizations
const ButtonAlt = (props) => {
  const theme = useTheme()

  return (
    <Button
      css={[
        generateButtonGradient({
          start: theme.brand.blue,
          stop: theme.brand.darkBlue,
        }),
        {
          color: theme.contrast.base,

          '&:hover, &:focus': {
            color: theme.contrast.base,
          },
        },
      ]}
      {...props}
    />
  )
}

const RequestDemoButton = (props) => (
  <ButtonAlt
    url="#request-demo"
    onClick={() => {
      openRequestDemoSection()
    }}
    {...props}
  >
    Request a demo
  </ButtonAlt>
)

const getTierBadgeStyles = ({ theme, tier }) => [
  {
    border: 0,
    color: theme.contrast.base,
  },

  tier === 'Free' && [gradient.tealGreen(theme)],
  tier === 'Pro' && {
    background: generateGradient({
      start: theme.brand.blue,
      stop: theme.brand.darkBlue,
    }),
  },
  tier === 'Enterprise' && {
    background: generateGradient({
      start: theme.brand.yellow,
      stop: theme.brand.red,
    }),
  },
]

const FeatureListItem = ({ children, ...rest }) => {
  const theme = useTheme()

  return (
    <div
      css={{
        display: 'grid',
        gridTemplateColumns: 'auto 1fr',
        gap: theme.space.base,
        alignItems: 'center',
        paddingLeft: theme.space.small,
      }}
      {...rest}
    >
      <Icon
        icon="check"
        css={{
          color: theme.color.primary.base,
          top: 'auto', // Reset default top offset in `<Icon />`
        }}
      />
      <div>{children}</div>
    </div>
  )
}

FeatureListItem.propTypes = {
  children: PropTypes.node.isRequired,
}

const TiersHeader = ({ tier, size = 'base' }) => {
  const theme = useTheme()

  return (
    <Fragment>
      <Badge css={[getTierBadgeStyles({ theme, tier: tier.title })]}>
        {tier.title}
      </Badge>
      <div
        css={{
          fontWeight: theme.fontWeight.bold,
          marginTop: theme.space.small,
          fontSize: theme.fontSize.xsmall,
          textTransform: 'uppercase',
          letterSpacing: 'normal',
          color: theme.fontColor.muted,
        }}
      >
        {tier.sub}
      </div>
      <div
        css={{
          marginTop: size === 'base' ? theme.space.base : theme.space.small,
          marginBottom: theme.space.base,
        }}
      >
        <div
          css={{
            fontWeight: theme.fontWeight.bold,
            fontSize:
              size === 'base' ? theme.fontSize.xlarge : theme.fontSize.small,
          }}
        >
          {tier.price.amount}
        </div>
        <div
          css={{
            fontSize: theme.fontSize.xsmall,
            color: theme.fontColor.muted,
          }}
        >
          {tier.price.sub}
        </div>
      </div>
    </Fragment>
  )
}

TiersHeader.propTypes = {
  tier: PropTypes.object.isRequired,
  size: PropTypes.oneOf(['base', 'small']),
}

const TopTiersMobileCTA = ({ tier }) => {
  const theme = useTheme()

  const tierKey = tier.title.toLowerCase()

  return (
    <div
      css={{
        textAlign: 'center',

        [theme.mq.large]: {
          display: 'none',
        },
      }}
    >
      {tierKey === 'free' ? (
        <TryForFreeButton css={{ width: '100%' }} />
      ) : (
        <RequestDemoButton css={{ width: '100%' }} />
      )}
    </div>
  )
}

TopTiersMobileCTA.propTypes = {
  tier: PropTypes.object.isRequired,
}

const TopTiersDesktopCTAs = () => {
  const theme = useTheme()

  return (
    <div
      css={{
        display: 'grid',
        gridTemplateColumns: '1fr 2fr',
        gap: theme.space.small,
        textAlign: 'center',

        [theme.mq.medium__max]: {
          display: 'none',
        },
      }}
    >
      <div css={{ paddingTop: theme.space.large }}>
        <TryForFreeButton css={{ width: '100%' }} />
      </div>
      <div
        css={{
          position: 'relative',
          paddingTop: theme.space.large,
          overflow: 'hidden',
        }}
      >
        {/* Grid background to demo request button */}
        <div
          css={{
            position: 'absolute',
            left: '50%',
            transform: 'translateX(-50%)',
            width: '70%',
            borderRadius: theme.borderRadius.base,
            border: theme.border.base,
            height: theme.height.base * 2,
            zIndex: 0,
            bottom: theme.height.base / 2,
          }}
        />
        <RequestDemoButton
          css={{
            minWidth: '33.333333%',
            boxShadow: `0 0 0 10px ${theme.color.panel.base}`,
            position: 'relative',
            zIndex: 1,
          }}
        />
      </div>
    </div>
  )
}

const TopTiers = () => {
  const theme = useTheme()

  return (
    <Section css={{ background: theme.color.panel.base }}>
      <Container
        size="extended"
        css={{
          // Offset padding taken care of inside scrollable list
          [theme.mq.medium__max]: {
            padding: 0,
          },
        }}
      >
        <div
          css={{
            [theme.mq.large]: {
              zIndex: 1,
              position: 'relative',
              marginTop: -StylesPricingVariables(theme).heroOffset,
            },
          }}
        >
          <div
            css={{
              display: 'grid',
              gridTemplateColumns: `repeat(3, 1fr)`,
              gap: theme.space.small,

              // 1. Offset so the overflow container does not cut-off the `boxShadow` on each card
              [theme.mq.medium__max]: {
                paddingLeft: theme.space.base,
                gridTemplateColumns: `repeat(3, minmax(280px, 1fr)) ${theme.space.small}px`,
                overflowX: 'auto',
                maxWidth: '100%',
                paddingTop: theme.space.small, // 1
                position: 'relative',
                maskImage: `linear-gradient(
                  90deg,
                  transparent,
                  ${theme.color.light.base} 15%,
                  ${theme.color.light.base} 85%,
                  transparent
                )`,
              },
            }}
          >
            {tiers.map((tier) => (
              <div
                css={{
                  display: 'grid',
                  gridTemplateRows: '1fr auto',

                  [theme.mq.medium__max]: {
                    gap: theme.space.base,
                  },
                }}
                key={tier.title}
              >
                <div
                  css={{
                    background: theme.color.light.base,
                    borderRadius: theme.borderRadius.base,
                    padding: theme.space.base,
                    display: 'grid',
                    gridTemplateRows: 'auto 1fr',
                    gap: theme.space.base,
                    boxShadow: theme.boxShadow.large,
                  }}
                >
                  <header
                    css={{
                      textAlign: 'center',
                    }}
                  >
                    <TiersHeader tier={tier} />
                    <p
                      css={{
                        fontSize: theme.fontSize.small,
                      }}
                    >
                      {tier.description}
                    </p>
                  </header>
                  <List
                    border
                    space="small"
                    css={{
                      fontSize: theme.fontSize.small,
                      margin: 0,
                      alignSelf: 'center',
                    }}
                  >
                    {tier.features.map((feature) => (
                      <ListItem key={feature}>
                        <FeatureListItem>
                          <FeatureValue value={feature} />
                        </FeatureListItem>
                      </ListItem>
                    ))}
                  </List>
                </div>
                <TopTiersMobileCTA tier={tier} />
              </div>
            ))}
            {/* Dummy div for extra scrolling on mobile */}
            <div
              css={{
                [theme.mq.large]: {
                  display: 'none',
                },
              }}
            />
          </div>
          <TopTiersDesktopCTAs />
        </div>
      </Container>
    </Section>
  )
}

const requestDemoSchema = yup.object().shape({
  'bot-field': yup.string().max(0),
  tier: yup.string().required().trim().label('Tier'),
  company: yup.string().required().trim().label('Company'),
  email: yup
    .string()
    .required()
    .email('Please provide a valid email')
    .corporateEmail()
    .trim()
    .label('Email'),
  firstName: yup.string().required().trim().label('First name'),
  lastName: yup.string().required().trim().label('Last name'),
  phone: yup.string().required().trim().label('Phone'),
  jobTitle: yup.string().required().trim().label('Job title'),
})

const initialValues = {
  'form-name': 'pricing-demo',
  'bot-field': '',
  tier: '',
  company: '',
  email: '',
  firstName: '',
  lastName: '',
  phone: '',
  jobTitle: '',
}

const RequestDemoForm = () => {
  const theme = useTheme()

  const [response, setResponse] = useState()

  const formId = 'pricing-demo'

  const handleSubmit = async (values, { setSubmitting, setFieldError }) => {
    setResponse(null)
    setSubmitting(true)

    const { error } = await phoneValidator({
      phone: values.phone,
      setFieldError,
    })

    // If there's an error from the phone validator; bail.
    // @NOTE We set the field error in that utility
    if (error) {
      return
    }

    const [, success] = await to(
      axios({
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        data: qs.stringify(values),
        url: '/',
      })
    )

    setSubmitting(false)

    if (success) {
      track('Demo requested', {
        label: `netlify-${formId}`,
      })

      setResponse('success')
    } else {
      setResponse('error')
    }
  }

  return (
    <div
      id="request-demo"
      css={{
        background: theme.color.panel.base,
        paddingTop: theme.space.xlarge,
        paddingBottom: theme.space.xlarge,
        display: 'none', // Hiding by default (triggered by CTA buttons)
      }}
    >
      <Container>
        <div
          css={{
            display: 'grid',
            gridTemplateColumns: `${theme.fontSize.base}px 1fr ${theme.fontSize.base}px`,
            gap: theme.space.small,
          }}
        >
          <div />
          <SectionTitle
            as="h4"
            title="See for yourself how Gremlin helps you build more reliable systems"
          />
          <Close onClick={closeRequestDemoSection} />
        </div>

        {response === 'success' && (
          <Alert type="primary">
            Thank you for requesting a demo of Gremlin!
          </Alert>
        )}

        {response === 'error' && (
          <Alert type="danger">
            There was an issue with your submission. Please try again later.
          </Alert>
        )}
        {!response && (
          <Formik
            initialValues={initialValues}
            validationSchema={requestDemoSchema}
            onSubmit={handleSubmit}
          >
            {({ isSubmitting }) => (
              <Form
                name={formId}
                data-netlify="true"
                data-netlify-honeypot="bot-field"
                noValidate
              >
                <input type="hidden" name="form-name" value="pricing-demo" />
                <BotField name="bot-field" css={misc.hide} type="text" />
                <div
                  css={{
                    display: 'grid',
                    gap: theme.space.base,

                    [theme.mq.medium]: {
                      gridTemplateColumns: 'repeat(2, 1fr)',
                    },
                  }}
                >
                  <div>
                    <FormikField
                      required
                      label="Which tier are you interested in?"
                      name="tier"
                      as="select"
                    >
                      <option value="">---</option>
                      <option value="free">Free</option>
                      <option value="pro">Pro</option>
                      <option value="enterprise">Enterprise</option>
                    </FormikField>
                  </div>
                  <div>
                    <FormikField
                      required
                      name="company"
                      as="input"
                      label="Company"
                    />
                  </div>
                  <div>
                    <FormikField
                      required
                      name="email"
                      as="input"
                      type="email"
                      label="Company email"
                    />
                  </div>

                  <div>
                    <FormikField
                      required
                      name="firstName"
                      as="input"
                      label="First name"
                    />
                  </div>
                  <div>
                    <FormikField
                      required
                      name="lastName"
                      as="input"
                      label="Last name"
                    />
                  </div>
                  <div>
                    <FormikField
                      required
                      name="phone"
                      as="input"
                      label="Phone"
                    />
                  </div>
                  <div>
                    <FormikField
                      required
                      name="jobTitle"
                      as="input"
                      label="Job title"
                    />
                  </div>
                </div>

                <div
                  css={{ marginTop: theme.space.large, textAlign: 'center' }}
                >
                  <ButtonAlt loading={isSubmitting} actionType="submit">
                    Request a demo
                  </ButtonAlt>
                </div>
              </Form>
            )}
          </Formik>
        )}

        <div css={{ marginTop: theme.space.xlarge, textAlign: 'center' }}>
          <Button
            type="reset"
            css={[
              link.muted(theme),
              {
                letterSpacing: theme.letterSpacing.extended,
                fontSize: theme.fontSize.medium,
                fontWeight: theme.fontWeight.bold,
                textTransform: 'uppercase',
              },
            ]}
            url="#features"
          >
            Compare all plans{' '}
            <Icon
              size="small"
              icon="caret-down"
              css={{ marginLeft: theme.space.small }}
            />
          </Button>
        </div>
      </Container>
    </div>
  )
}

const FeatureSectionTitle = (props) => {
  const theme = useTheme()

  return (
    <div
      css={{
        fontWeight: theme.fontWeight.bold,
        background: theme.color.panel.base,
        gridColumn: '1 / span 4',
        padding: theme.space.base,
      }}
      {...props}
    />
  )
}

const FeatureCell = forwardRef(({ isLabel, hasTooltip, ...rest }, ref) => {
  const theme = useTheme()

  return (
    <div
      ref={ref}
      css={[
        hasTooltip && {
          position: 'relative',
          cursor: 'help',

          '&::after': {
            content: "''",
            backgroundImage: `url(${questionCircle})`,
            backgroundPosition: 'center center',
            backgroundSize: 'contain',
            backgroundRepeat: 'no-repeat',
            fill: theme.fontColor.filter,
            opacity: theme.opacity.base / 2,
            width: theme.fontSize.small,
            height: theme.fontSize.small,
            position: 'absolute',
            top: '50%',
            transform: 'translateY(-50%)',
            right: theme.space.small,
            zIndex: 1,
          },
        },

        !isLabel && {
          textAlign: 'center',
        },

        {
          padding: theme.space.base,
          background: theme.color.light.base,
        },
      ]}
      {...rest}
    />
  )
})

FeatureCell.propTypes = {
  isLabel: PropTypes.bool,
  hasTooltip: PropTypes.bool,
}

const FeatureValue = ({ value }) => {
  const theme = useTheme()

  if (typeof value === 'object') {
    return (
      <TextLink css={[link.reset(theme), text.underline]} href={value[1]}>
        {value[0]}
      </TextLink>
    )
  }

  if (typeof value === 'string' || Number.isInteger(value))
    return <Fragment>{value}</Fragment>

  if (value)
    return <Icon icon="check" css={{ color: theme.color.primary.base }} />

  return (
    <Fragment>
      <span css={{ color: theme.fontColor.muted }}>&mdash;</span>
    </Fragment>
  )
}

FeatureValue.propTypes = {
  value: PropTypes.any,
}

const MobileFeatures = () => {
  const theme = useTheme()

  return (
    <div
      css={{
        display: 'grid',
        gridTemplateColumns: `repeat(3, minmax(280px, 1fr)) ${theme.space.small}px`,
        gap: theme.space.small,
        paddingLeft: theme.space.base,
        overflowX: 'auto',
        maxWidth: '100%',
        maskImage: `linear-gradient(
            90deg,
            transparent,
            ${theme.color.light.base} 15%,
            ${theme.color.light.base} 85%,
            transparent
          )`,

        [theme.mq.large]: {
          display: 'none',
        },
      }}
    >
      {tiers.map((tier) => {
        const tierKey = tier.title.toLowerCase()

        return (
          <div key={tier.title} css={{ background: theme.color.light.base }}>
            <header
              css={{
                textAlign: 'center',
                paddingTop: theme.space.medium,
                paddingLeft: theme.space.base,
                paddingRight: theme.space.base,
                paddingBottom: theme.space.medium,
                border: theme.border.base,
                borderTopLeftRadius: theme.borderRadius.base,
                borderTopRightRadius: theme.borderRadius.base,
              }}
            >
              <TiersHeader tier={tier} size="small" />
            </header>
            <div
              css={{
                display: 'grid',
                background: theme.color.border.base,
                gridTemplateColumns: 'repeat(2, 1fr)',
                gap: 1,
                border: theme.border.base,
                borderTop: 0,
                fontSize: theme.fontSize.small,
              }}
            >
              {features.map((feature) => (
                <Fragment key={feature.title}>
                  {/* Section title */}
                  <FeatureSectionTitle css={{ gridColumn: '1 / span 2' }}>
                    {feature.title}
                  </FeatureSectionTitle>
                  {feature.items.map((item) => (
                    <Fragment key={item.title}>
                      <FeatureCell isLabel>{item.title}</FeatureCell>
                      <FeatureCell>
                        <FeatureValue value={item.values[tierKey]} />
                      </FeatureCell>
                    </Fragment>
                  ))}
                </Fragment>
              ))}
            </div>
            <div
              css={{
                padding: theme.space.base,
                textAlign: 'center',
                border: theme.border.base,
                borderTop: 0,
                borderBottomLeftRadius: theme.borderRadius.base,
                borderBottomRightRadius: theme.borderRadius.base,
              }}
            >
              {tierKey === 'free' ? (
                <TryForFreeButton />
              ) : (
                <RequestDemoButton />
              )}
            </div>
          </div>
        )
      })}
      {/* Dummy div for extra scrolling on mobile */}
      <div
        css={{
          [theme.mq.large]: {
            display: 'none',
          },
        }}
      />
    </div>
  )
}

const DesktopFeatures = () => {
  const theme = useTheme()

  const tierListTriggerRef = useRef()

  const tierListIntersection = useIntersection(tierListTriggerRef, {
    rootMargin: `-${StylesHeaderVariables(theme).height}px`,
    threshold: 0,
  })

  const isSticky = tierListIntersection?.intersectionRatio === 0

  return (
    <div
      css={{
        position: 'relative',

        // 1. Illustration dimensions
        '&::after': {
          content: "''",
          width: 450, // 1
          height: 340, // 1
          background: `url(${tableBlob}) no-repeat`,
          backgroundSize: 'contain',
          position: 'absolute',
          bottom: -100,
          left: -100,
          zIndex: -1,
        },

        [theme.mq.medium__max]: {
          display: 'none',
        },
      }}
    >
      {/* This wrapper div contains our sticky headers to just these children */}
      <div
        css={{
          position: 'relative',
          zIndex: 2,
        }}
      >
        {/* Used as a trigger point to work with `position: sticky` elements */}
        <div
          ref={tierListTriggerRef}
          css={{ height: 1, visibility: 'hidden' }}
        />
        <div
          css={[
            {
              display: 'grid',
              gridTemplateColumns: 'repeat(3, 1fr)',
              gap: 1,
              background: theme.color.border.base,
              position: 'sticky',
              top: StylesHeaderVariables(theme).height,
              border: theme.border.base,
              zIndex: 1,
              marginLeft: '25%', // Offset width of 1 column from feature table below it
              marginBottom: -1,
            },

            !isSticky && {
              borderTopLeftRadius: theme.borderRadius.base,
              borderTopRightRadius: theme.borderRadius.base,
            },

            // Called when our sticky header kicks into 🏎 HIGH-GEAR 🏎
            isSticky && {
              boxShadow: theme.boxShadow.large,
            },
          ]}
        >
          {tiers.map((tier, index) => (
            <div
              key={tier.title}
              css={[
                {
                  textAlign: 'center',
                  background: theme.color.light.base,
                  paddingTop: theme.space.medium,
                  paddingLeft: theme.space.base,
                  paddingRight: theme.space.base,
                  paddingBottom: theme.space.medium,
                },

                !isSticky &&
                  index === 0 && {
                    borderTopLeftRadius: theme.borderRadius.base,
                  },

                !isSticky &&
                  index === 3 && {
                    borderTopRightRadius: theme.borderRadius.base,
                  },
              ]}
            >
              <TiersHeader tier={tier} size="small" />
            </div>
          ))}
        </div>
        <div
          css={{
            display: 'grid',
            background: theme.color.border.base,
            gridTemplateColumns: 'repeat(4, 1fr)',
            gap: 1,
            border: theme.border.base,
            fontSize: theme.fontSize.small,
            borderTopLeftRadius: theme.borderRadius.base,
            borderBottomLeftRadius: theme.borderRadius.base,
          }}
        >
          {features.map((feature, featureIndex) => (
            <Fragment key={feature.title}>
              {/* Section title */}
              <FeatureSectionTitle
                css={[
                  featureIndex === 0 && {
                    borderTopLeftRadius: theme.borderRadius.base,
                  },
                ]}
              >
                {feature.title}
              </FeatureSectionTitle>
              {feature.items.map((item, itemIndex) => (
                <Fragment key={item.title}>
                  {item.description ? (
                    <Tooltip
                      placement="right"
                      content={
                        <span css={{ fontSize: theme.fontSize.xsmall }}>
                          {item.description}
                        </span>
                      }
                    >
                      <FeatureCell
                        isLabel
                        hasTooltip
                        css={[
                          featureIndex === features.length - 1 &&
                            itemIndex === feature.items.length - 1 && {
                              borderBottomLeftRadius: theme.borderRadius.base,
                            },
                        ]}
                      >
                        {item.title}
                      </FeatureCell>
                    </Tooltip>
                  ) : (
                    <FeatureCell
                      isLabel
                      css={[
                        featureIndex === features.length - 1 &&
                          itemIndex === feature.items.length - 1 && {
                            borderBottomLeftRadius: theme.borderRadius.base,
                          },
                      ]}
                    >
                      {item.title}
                    </FeatureCell>
                  )}
                  {Object.entries(item.values).map(([key, value]) => (
                    <FeatureCell key={`${key}-${value}`}>
                      <FeatureValue value={value} />
                    </FeatureCell>
                  ))}
                </Fragment>
              ))}
            </Fragment>
          ))}
        </div>
      </div>
      <div
        css={{
          display: 'grid',
          gridTemplateColumns: 'repeat(3, 1fr)',
          gap: 1,
          background: theme.color.border.base,
          border: theme.border.base,
          borderTop: 0,
          zIndex: 1,
          marginLeft: '25%', // Offset width of 1 column from feature table above it
          textAlign: 'center',
          borderBottomLeftRadius: theme.borderRadius.base,
          borderBottomRightRadius: theme.borderRadius.base,
          position: 'relative',

          // 1. Illustration dimensions
          '&::after': {
            content: "''",
            width: 75, // 1
            height: 66, // 1
            background: `url(${tableBlobSmall}) no-repeat`,
            backgroundSize: 'contain',
            position: 'absolute',
            bottom: -25,
            right: -25,
            zIndex: -1,
          },
        }}
      >
        <FeatureCell
          css={{
            paddingLeft: theme.space.xsmall,
            paddingRight: theme.space.xsmall,
            borderBottomLeftRadius: theme.borderRadius.base,
          }}
        >
          <TryForFreeButton />
        </FeatureCell>
        <FeatureCell
          css={{
            paddingLeft: theme.space.xsmall,
            paddingRight: theme.space.xsmall,
            gridColumn: '2 / span 3',
            borderBottomRightRadius: theme.borderRadius.base,
          }}
        >
          <RequestDemoButton />
        </FeatureCell>
      </div>
    </div>
  )
}

const AddonPackages = () => {
  const theme = useTheme()

  return (
    <div
      css={{
        marginTop: theme.space.xlarge,

        [theme.mq.large]: {
          paddingLeft: '20%',
        },
      }}
    >
      <h4 css={{ textAlign: 'center', marginBottom: theme.space.large }}>
        Add-on packages
      </h4>
      <div
        css={{
          display: 'grid',
          gap: theme.space.large,

          [theme.mq.small]: {
            gridTemplateColumns: 'repeat(2, 1fr)',
          },

          [theme.mq.large]: {
            gap: theme.space.xlarge,
          },
        }}
      >
        {addons.map((addon) => (
          <div key={addon.title}>
            <header
              css={{ textAlign: 'center', marginBottom: theme.space.medium }}
            >
              <Badge css={[getTierBadgeStyles({ theme, tier: addon.title })]}>
                {addon.title}
              </Badge>
            </header>
            <List border space="small" css={{ fontSize: theme.fontSize.small }}>
              {addon.items.map((item) => (
                <ListItem key={item}>
                  <FeatureListItem>{item}</FeatureListItem>
                </ListItem>
              ))}
            </List>
            {addon.meta && (
              <div
                css={{
                  marginTop: theme.space.base,
                  textAlign: 'center',
                  fontSize: theme.fontSize.xsmall,
                  color: theme.fontColor.muted,
                }}
              >
                {addon.meta}
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  )
}

const AllFeatures = () => {
  const theme = useTheme()

  return (
    <Section
      id="features"
      css={{
        paddingBottom: '0 !important',
      }}
    >
      <Container
        size="extended"
        css={{
          // Offset padding taken care of inside scrollable list
          [theme.mq.medium__max]: {
            padding: 0,
          },
        }}
      >
        <SectionTitle as="h2" title="All features" />
        <DesktopFeatures />
        <MobileFeatures />
      </Container>
      <Container size="extended">
        <AddonPackages />
      </Container>
    </Section>
  )
}

const FAQ = ({ faq }) => {
  const theme = useTheme()

  return (
    <Section css={{ paddingTop: '0 !important' }}>
      <Container>
        <div
          css={{
            display: 'grid',
            gap: theme.space.large,

            [theme.mq.medium]: {
              gridTemplateColumns: '5fr 7fr',
              gap: theme.space.xlarge,
            },
          }}
        >
          <div
            css={{
              textAlign: 'center',

              [theme.mq.medium]: {
                textAlign: 'left',
              },
            }}
          >
            <div
              css={{
                [theme.mq.medium]: [stickyIt(theme)],
              }}
            >
              <h3 css={{ marginBottom: theme.space.large }}>
                <Data>{faq.heading}</Data>
              </h3>
              <div
                css={{
                  color: theme.color.primary.base,
                  marginBottom: theme.space.base,
                  fontSize: theme.fontSize.large,
                  fontWeight: theme.fontWeight.bold,
                }}
              >
                <Data>{faq.blurb}</Data>
              </div>
              <ButtonLink type="primary" url={faq.cta.url}>
                <Data>{faq.cta.label}</Data>
              </ButtonLink>
            </div>
          </div>
          <div>
            {faq.sections.map((section) => (
              <Fragment key={section.items}>
                {section.heading && (
                  <h4
                    css={{
                      marginBottom: theme.space.large,
                      fontSize: theme.fontSize.xlarge,
                    }}
                  >
                    {section.heading}
                  </h4>
                )}
                <List space="medium">
                  {section.items.map((item) => (
                    <ListItem key={item.question}>
                      <div
                        css={{
                          fontSize: theme.fontSize.large,
                          fontWeight: theme.fontWeight.bold,
                        }}
                      >
                        <Data>{item.question}</Data>
                      </div>
                      <div css={{ marginTop: theme.space.small }}>
                        <Data>{item.answer}</Data>
                      </div>
                    </ListItem>
                  ))}
                </List>
              </Fragment>
            ))}
          </div>
        </div>
      </Container>
    </Section>
  )
}

FAQ.propTypes = {
  faq: PropTypes.object.isRequired,
}

const Pricing = ({
  data: {
    file: {
      childMdx: { frontmatter: pageData },
    },
  },
}) => {
  const theme = useTheme()

  return (
    <BaseLayout>
      <HelmetSEO title={pageData.title} description={pageData.description} />
      <Hero
        css={[
          gradient.dark(theme),
          {
            '&::after': {
              content: "''",
              width: '100%',
              height: '100%',
              background: `url(${heroBlob}) no-repeat`,
              backgroundSize: 'contain',
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              zIndex: 0,
            },

            [theme.mq.large]: {
              paddingBottom: `${
                StylesPricingVariables(theme).heroOffset
              }px !important`,

              '&::after': {
                // 1. Illustration dimensions

                width: 519, // 1
                height: 350, // 1
                top: 'auto',
                transform: 'none',
                bottom: 50,
                left: 100,
              },
            },
          },
        ]}
      >
        <HeroContent>
          <Container>
            <HeroTitleWrapper>
              <HeroTitle size="large">
                <Data>{pageData.hero.heading}</Data>
              </HeroTitle>
              <HeroTitleSub>
                <Data>{pageData.hero.blurb}</Data>
              </HeroTitleSub>
            </HeroTitleWrapper>
          </Container>
        </HeroContent>
      </Hero>
      <TopTiers />
      <RequestDemoForm />
      <AllFeatures />
      <ProductSection>
        <Container>
          <SectionTitle
            as="h2"
            title={<Data>{pageData.free.heading}</Data>}
            sub={<Data>{pageData.free.blurb}</Data>}
          />
          <ProductGridBlock size="large">
            <div
              css={{
                width: '100%',
                textAlign: 'center',
                position: 'relative',

                '&::before': {
                  content: "''",
                  background: `url(${freeBlob}) no-repeat center center`,
                  backgroundSize: 'contain',
                  position: 'absolute',
                  width: '150%',
                  height: '150%',
                  transform: 'translate(-50%, -50%)',
                  top: '35%',
                  left: '50%',
                  zIndex: -1,
                  display: 'none',

                  [theme.mq.medium]: {
                    display: 'block',
                  },
                },

                [theme.mq.medium]: {
                  gridColumn: '1 / span 4',
                  gridRow: 1,
                },
              }}
            >
              <FreeSVG
                css={{
                  maxWidth: 400,

                  [theme.mq.medium]: {
                    maxWidth: 550,
                  },
                }}
              />
            </div>

            <ProductGridBlockContent>
              <GradientList items={pageData.free.items} />
            </ProductGridBlockContent>
          </ProductGridBlock>
          <div
            css={{
              marginTop: theme.space.large,
              textAlign: 'center',

              [theme.mq.medium]: {
                marginTop: theme.space.xlarge,
              },
            }}
          >
            <ButtonLink type="primary" url={pageData.free.cta.url}>
              {pageData.free.cta.label}
            </ButtonLink>
          </div>
        </Container>
      </ProductSection>
      <FAQ faq={pageData.faq} />
    </BaseLayout>
  )
}

Pricing.propTypes = {
  data: PropTypes.object.isRequired,
}

export default Pricing

export const pageQuery = graphql`
  query PricingPageData {
    file(name: { eq: "pricing" }, absolutePath: { regex: "/src/data/" }) {
      childMdx {
        frontmatter {
          title
          description
          hero {
            blurb
            heading
          }
          free {
            heading
            blurb
            items {
              blurb
            }
            cta {
              label
              url
            }
          }
          faq {
            heading
            blurb
            sections {
              heading
              items {
                question
                answer
              }
            }
            cta {
              label
              url
            }
          }
        }
      }
    }
  }
`
